// src/components/ImageUploader/ImageUploader.jsx
import React, { useRef, useState } from 'react';
import './ImageUploader.css';

const ImageUploader = ({ onImageLoad, currentImage }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelect = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF)');
      return;
    }

    setIsLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        onImageLoad(e.target.result, {
          width: img.width,
          height: img.height,
          name: file.name,
          size: file.size
        });
        setIsLoading(false);
      };
      img.onerror = () => {
        alert('Erro ao carregar a imagem. Tente novamente.');
        setIsLoading(false);
      };
      img.src = e.target.result;
    };
    reader.onerror = () => {
      alert('Erro ao ler o arquivo. Tente novamente.');
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    if (confirm('Tem certeza que deseja remover a imagem atual?')) {
      onImageLoad(null, null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="image-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="file-input-hidden"
      />

      {!currentImage ? (
        <div 
          className={`upload-zone ${isDragging ? 'dragging' : ''} ${isLoading ? 'loading' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          {isLoading ? (
            <div className="loading-content">
              <div className="spinner"></div>
              <h3>Carregando imagem...</h3>
              <p>Aguarde enquanto processamos sua planta</p>
            </div>
          ) : (
            <div className="upload-content">
              <svg className="upload-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M7 10L12 15L17 10" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M12 15V3" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              
              <h3>Arraste a planta aqui</h3>
              <p>ou <span className="click-text">clique para selecionar</span></p>
              
              <div className="supported-formats">
                <span>Formatos suportados:</span>
                <div className="format-badges">
                  <span className="format-badge">JPG</span>
                  <span className="format-badge">PNG</span>
                  <span className="format-badge">GIF</span>
                  <span className="format-badge">WEBP</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="image-preview">
          <div className="image-header">
            <div className="image-info">
              <h4>📋 Planta Carregada</h4>
              <div className="image-details">
                <span>Resolução: {currentImage.metadata?.width} × {currentImage.metadata?.height}px</span>
                <span>Tamanho: {formatFileSize(currentImage.metadata?.size || 0)}</span>
                <span>Arquivo: {currentImage.metadata?.name}</span>
              </div>
            </div>
            
            <div className="image-actions">
              <button 
                className="btn btn-change"
                onClick={openFileDialog}
                title="Trocar imagem"
              >
                🔄 Trocar
              </button>
              <button 
                className="btn btn-remove"
                onClick={removeImage}
                title="Remover imagem"
              >
                🗑️ Remover
              </button>
            </div>
          </div>
          
          <div className="image-thumbnail">
            <img 
              src={currentImage.url} 
              alt="Preview da planta" 
              className="thumbnail-image"
            />
          </div>
          
          <div className="usage-tips">
            <h5>💡 Dicas de uso:</h5>
            <ul>
              <li>Clique na planta para posicionar pontos de luz</li>
              <li>Use as coordenadas em porcentagem (%) para melhor adaptação</li>
              <li>Configure os parâmetros no painel lateral antes de clicar</li>
              <li>Você pode remover pontos clicando neles quando selecionados</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;